import React from 'react';

import { Link } from 'react-router-dom';

import { PlusOutlined } from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { TenantIntegrationStatus } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantDTO } from '@recurrency/core-api-schema/dist/tenants/tenantDTO';
import { ColumnType } from 'antd/lib/table';
import { fuzzyFilter } from 'fuzzbunny';
import { ZipCelXSheet } from 'zipcelx';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { Button } from 'components/Button';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpacer } from 'components/FlexSpacer';
import { Input } from 'components/Input';
import { ButtonLink } from 'components/Links';
import { CenteredError, CenteredLoader } from 'components/Loaders';
import { PageHeader } from 'components/PageHeader';
import { DownloadButton, DownloadXSheetColumn, recordsToXSheet } from 'components/recipes/DownloadButton';
import { Table } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';

import { formatDate } from 'utils/formatting';
import { objKeys, objMapValues } from 'utils/object';
import { routes, useHashState } from 'utils/routes';
import { asKeyOf, sortableBooleanColumn, sortableNumberColumn, sortableStringColumn } from 'utils/tables';

const MAX_RECORDS = 1000;

export interface TenantsListHashState {
  query?: string;
  erpTypes?: string[];
  tenantIsActive?: string[];
  integrationStatuses?: TenantIntegrationStatus[];
  motherduckPartitions?: string[];
  featureFlags?: string[];
  productFlags?: string[];
}

export const TenantListPage = () => {
  const [hashState, updateHashState] = useHashState<TenantsListHashState>();
  const { integrationStatuses, motherduckPartitions, tenantIsActive, erpTypes, featureFlags, productFlags } = hashState;

  const {
    data: tenantData,
    error,
    isLoading,
  } = useCoreApi(schemas.tenants.getAllTenants, {
    queryParams: { limit: MAX_RECORDS },
  });

  // filter tenants
  const tenants = tenantData?.items || [];
  let filteredTenants = tenants;
  filteredTenants = fuzzyFilter(filteredTenants, hashState.query ?? '', {
    fields: ['id', 'name', 'slug', 'id', 'databaseSchema', 'erpType'],
  }).map(({ item }) => item);
  if (integrationStatuses?.length) {
    filteredTenants = filteredTenants.filter((t) => integrationStatuses.includes(t.integrations[0]?.status));
  }
  if (motherduckPartitions?.length) {
    filteredTenants = filteredTenants.filter((t) => motherduckPartitions.includes(t.motherduckPartition));
  }
  if (erpTypes?.length) {
    filteredTenants = filteredTenants.filter((t) => erpTypes.includes(t.erpType));
  }
  if (tenantIsActive?.length) {
    const isActive = tenantIsActive.map((s) => s === 'active');
    filteredTenants = filteredTenants.filter((t) => isActive.includes(t.isActive));
  }
  if (featureFlags?.length) {
    filteredTenants = filteredTenants.filter((t) =>
      featureFlags.every((flag) => t.featureFlags && t.featureFlags[flag as keyof typeof t.featureFlags]),
    );
  }
  if (productFlags?.length) {
    filteredTenants = filteredTenants.filter((t) =>
      productFlags.every((flag) => t.products && t.products[flag as keyof typeof t.products]?.backendEnabled),
    );
  }

  const columns: ColumnType<TenantDTO>[] = [
    sortableNumberColumn({
      title: 'Tenant ID',
      dataIndex: asKeyOf<TenantDTO>('id'),
      render: (id: string) => <Link to={routes.internal.tenantDetails(id)}>{id}</Link>,
    }),
    sortableStringColumn({
      title: 'Name',
      dataIndex: asKeyOf<TenantDTO>('name'),
    }),
    sortableStringColumn({
      title: 'Slug',
      dataIndex: asKeyOf<TenantDTO>('slug'),
    }),
    sortableStringColumn({
      title: 'DB Schema',
      dataIndex: asKeyOf<TenantDTO>('databaseSchema'),
    }),
    sortableStringColumn({
      title: 'Erp Type',
      dataIndex: asKeyOf<TenantDTO>('erpType'),
    }),
    sortableBooleanColumn({
      title: 'Active',
      dataIndex: asKeyOf<TenantDTO>('isActive'),
      filtered: true,
    }),
    sortableStringColumn({
      title: 'Integration Status',
      dataIndex: [asKeyOf<TenantDTO>('integrations'), 0, 'status'],
    }),
    sortableStringColumn({
      title: 'MD Partition',
      dataIndex: asKeyOf<TenantDTO>('motherduckPartition'),
    }),
    {
      title: 'Created At',
      dataIndex: asKeyOf<TenantDTO>('createdAt'),
      render: (date: string) => formatDate(date),
      sorter: (a: TenantDTO, b: TenantDTO) => a.createdAt.localeCompare(b.createdAt),
      sortDirections: ['descend', 'ascend'],
    },
    {
      render: ({ id }: { id: string }) => ButtonLink(routes.internal.tenantDetails(id)),
    },
  ];

  // async multi select options

  const countByIntegrationStatus = filteredTenants.reduce((acc, tenant) => {
    const status = tenant.integrations?.[0]?.status;
    if (status) acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<TenantIntegrationStatus, number>);

  const integrationStatusOptions = objKeys(TenantIntegrationStatus).map((integrationStatus) => ({
    label: TenantIntegrationStatus[integrationStatus],
    value: TenantIntegrationStatus[integrationStatus],
    count: countByIntegrationStatus[TenantIntegrationStatus[integrationStatus]] || 0,
  }));

  const motherduckPartitionOptions = Object.entries(
    filteredTenants.reduce((acc, tenant) => {
      const partition = tenant.motherduckPartition;
      if (partition) acc[partition] = (acc[partition] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  )
    .map(([value, count]) => ({ label: value, value, count }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const erpTypeOptions = Object.entries(
    filteredTenants.reduce((acc, tenant) => {
      const { erpType } = tenant;
      if (erpType) acc[erpType] = (acc[erpType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  )
    .map(([value, count]) => ({ label: value, value, count }))
    .sort((a, b) => b.count - a.count);

  const isActiveOptions = Object.entries(
    filteredTenants.reduce((acc, tenant) => {
      const isActive = tenant.isActive ? 'active' : 'inactive';
      acc[isActive] = (acc[isActive] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  )
    .map(([value, count]) => ({ label: value, value, count }))
    .sort((a, b) => b.count - a.count);

  const featureFlagOptions = Object.entries(
    filteredTenants.reduce((acc, tenant) => {
      for (const [featureFlag, isEnabled] of Object.entries(tenant.featureFlags || {})) {
        acc[featureFlag] = acc[featureFlag] || 0;
        if (isEnabled) acc[featureFlag] += 1;
      }
      return acc;
    }, {} as Record<string, number>),
  )
    .map(([value, count]) => ({ label: value, value, count }))
    .sort((a, b) => b.count - a.count);

  const productFlagOptions = Object.entries(
    filteredTenants.reduce((acc, tenant) => {
      for (const [productFlag, productConf] of Object.entries(tenant.products || {})) {
        acc[productFlag] = acc[productFlag] || 0;
        if (productConf.backendEnabled) acc[productFlag] += 1;
      }
      return acc;
    }, {} as Record<string, number>),
  )
    .map(([value, count]) => ({ label: value, value, count }))
    .sort((a, b) => b.count - a.count);

  if (error) {
    return <CenteredError error={error} />;
  }

  if (isLoading || !tenantData) {
    return <CenteredLoader />;
  }

  return (
    <Container>
      <PageHeader
        title="Tenants"
        subtitle={
          <>
            {countByIntegrationStatus[TenantIntegrationStatus.Active] +
              countByIntegrationStatus[TenantIntegrationStatus.Paused]}{' '}
            integrated tenants (Active + Paused) out of {tenantData.items.length} total tenants
          </>
        }
      />
      <FilterBarBox>
        <Input
          placeholder="Search Tenants"
          onChange={(ev) => updateHashState({ query: ev.target.value })}
          value={hashState.query}
          style={{ maxWidth: '200px' }}
          size="small"
          allowClear
        />
        <AsyncMultiSelect
          label="Status"
          selectProps={{ options: integrationStatusOptions }}
          selectedValues={hashState.integrationStatuses ?? []}
          onSelectedValuesChange={(values) =>
            updateHashState({ integrationStatuses: values as TenantIntegrationStatus[] })
          }
        />
        <AsyncMultiSelect
          label="Active"
          selectProps={{ options: isActiveOptions }}
          selectedValues={hashState.tenantIsActive ?? []}
          onSelectedValuesChange={(values) => updateHashState({ tenantIsActive: values })}
        />
        <AsyncMultiSelect
          label="ERP"
          selectProps={{ options: erpTypeOptions }}
          selectedValues={hashState.erpTypes ?? []}
          onSelectedValuesChange={(values) => updateHashState({ erpTypes: values })}
        />
        <AsyncMultiSelect
          label="Partition"
          selectProps={{ options: motherduckPartitionOptions }}
          selectedValues={hashState.motherduckPartitions ?? []}
          onSelectedValuesChange={(values) => updateHashState({ motherduckPartitions: values })}
        />
        <AsyncMultiSelect
          label="Product"
          selectProps={{ options: productFlagOptions }}
          selectedValues={hashState.productFlags ?? []}
          onSelectedValuesChange={(values) => updateHashState({ productFlags: values })}
          queryPlaceholder="Search products"
        />
        <AsyncMultiSelect
          label="Feature Flag"
          selectProps={{ options: featureFlagOptions }}
          selectedValues={hashState.featureFlags ?? []}
          onSelectedValuesChange={(values) => updateHashState({ featureFlags: values })}
          queryPlaceholder="Search feature flags"
        />
        {Object.keys(hashState).length > 0 ? (
          <ActionButton
            onClick={() => updateHashState(objMapValues(hashState as Any, () => undefined))}
            label="Clear All"
          />
        ) : null}
        <FlexSpacer />
        <DownloadButton recordType="Tenants" getDownloadData={() => getDownloadData(filteredTenants)} />
        <Button type="primary">
          <Link to={routes.internal.tenantNew()}>
            <PlusOutlined /> New Tenant
          </Link>
        </Button>
      </FilterBarBox>
      <Table columns={columns} data={filteredTenants} rowKey="id" isLoading={isLoading} />
    </Container>
  );
};

async function getDownloadData(tenants: TenantDTO[]): Promise<ZipCelXSheet> {
  // sort the tenants by the erp type and then database schema, placing null values at the top
  const sortedTenants = tenants.sort((a: TenantDTO, b: TenantDTO) => {
    if (!a.erpType && !b.erpType) return a.databaseSchema.localeCompare(b.databaseSchema);
    if (!a.erpType) {
      return 1;
    }
    if (!b.erpType) {
      return -1;
    }
    return a.erpType.localeCompare(b.erpType) || a.databaseSchema.localeCompare(b.databaseSchema);
  });
  const exportColumns: Array<DownloadXSheetColumn<TenantDTO>> = [
    { title: 'Tenant ID', type: 'string', value: (record) => record.id },
    { title: 'Name', type: 'string', value: (record) => record.name },
    { title: 'Slug', type: 'string', value: (record) => record.slug },
    { title: 'DB Schema', type: 'string', value: (record) => record.databaseSchema },
    { title: 'Erp Type', type: 'string', value: (record) => record.erpType },
    { title: 'Active', type: 'string', value: (record) => (record.isActive ? 'Yes' : 'No') },
    {
      title: 'Integration Status',
      type: 'string',
      value: (record) => (record.integrations.length > 0 ? record.integrations[0].status : ''),
    },
    {
      title: 'Created At',
      type: 'string',
      value: (record) => new Date(record.createdAt).toLocaleDateString(),
    },
  ];

  return recordsToXSheet(sortedTenants, exportColumns);
}
